/**
 * http — the single typed HTTP communication point for the portal frontend.
 *
 * This is the HTTP analogue of the game-client's `nakamaService`: every domain
 * api (`api/<area>/<domain>/api.ts`) talks to the backend ONLY through this
 * module. It builds on `portalFetch` (which attaches the JWT and handles 401 →
 * logout) and adds: base-URL prefixing, JSON encode/decode, FormData support
 * (multipart uploads), empty-body handling, and normalized `ApiError` throwing.
 *
 *   http.get<T>('/campaigns/my-campaigns')
 *   http.post<T>('/campaigns/:id/activities/:a/complete', { result })
 *
 * Paths are absolute from the backend root and must start with '/'.
 */
import { portalFetch } from '../../services/portal-fetch';
import { getBackendApiUrl } from '../../config/environment';
import { ApiError } from './api-error';

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const init: RequestInit = { method };

  if (body !== undefined) {
    if (body instanceof FormData) {
      // Let the browser set the multipart boundary — do NOT set Content-Type.
      init.body = body;
    } else {
      init.headers = { 'Content-Type': 'application/json' };
      init.body = JSON.stringify(body);
    }
  }

  // portalFetch attaches the JWT and throws SessionExpiredError on 401.
  const res = await portalFetch(`${getBackendApiUrl()}${path}`, init);

  if (!res.ok) {
    throw await ApiError.fromResponse(res);
  }

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export const http = {
  get: <T>(path: string): Promise<T> => request<T>('GET', path),
  post: <T>(path: string, body?: unknown): Promise<T> => request<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown): Promise<T> => request<T>('PATCH', path, body),
  put: <T>(path: string, body?: unknown): Promise<T> => request<T>('PUT', path, body),
  del: <T>(path: string, body?: unknown): Promise<T> => request<T>('DELETE', path, body),
};
